import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { AccountInfo, EventMessage, EventType, InteractionRequiredAuthError, InteractionStatus, RedirectRequest } from "@azure/msal-browser";
import { filter, Subject, takeUntil } from "rxjs";
import { environment } from "../../environments/environment";
import { protectedResources } from '../auth-config';

// Defines a token claim.
export class Claim {
  id: number;
  claim: string;
  value: string;
}

// The AuthService acts as a facade over the MSAL services.
export interface IAuthService
{
  // Emits when the login status has changed.
  loginStatusChanged : Subject<boolean>;
  // Performs a login redirect.
  login() : void;
  // Performs a logout redirect.
  logout() : void;
  // Gets a value indicating if a user is logged in.
  isLoggedIn() : boolean;
  // Gets a collection of accounts for the current user.
  getAccounts() : AccountInfo[];
  // Gets the first account for the current user.
  getAccount() : AccountInfo;
  // Gets the active account for the current user.
  getActiveAccount() : AccountInfo;
  // Gets the first email address associated with the current user.
  getAccountEmailAddress() : string;
  getClaims() : Claim[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements IAuthService, OnDestroy {

  private readonly _destroying$ = new Subject<void>();

  public loginStatusChanged = new Subject<boolean>();


  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
              private broadcastService: MsalBroadcastService,
              private authService: MsalService) {

    // Subscribe to the broadcast service to listen for changes in authentication state.
    this.broadcastService.inProgress$
        .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            // No need to unsubscribe, since the subscription will end when the `destroying$` subject emits.
            takeUntil(this._destroying$)
        )
        .subscribe(() => {
          // Once authentication state is done/nothing (.None) else in progress, set an active account.
          this.checkAndSetActiveAccount();          
          this.loginStatusChanged.next(this.isLoggedIn());
        })

    // Logs a message when login is successful.
    this.broadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        )
        .subscribe((result: EventMessage) => {
          console.log(result);
        });

    // Logout  user when failed to acquire token silently (called by MSAL route guard/interceptor).
    this.broadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        )
        .subscribe((result: EventMessage) => {
          // When the error requires interaction, logout the user.
          const error = result.error;
          if (error instanceof InteractionRequiredAuthError) {
            this.logout();
          }
        });
  }
  ngOnDestroy(): void {
    this.loginStatusChanged.complete();
    
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout() {
    var request : RedirectRequest = {
      scopes: protectedResources.scopes,
      redirectUri: environment.baseClaimSubmissionWebUrl
    }

    this.authService.logoutRedirect(request);
  }
  
  getAccounts() : AccountInfo[] {
    return this.authService.instance.getAllAccounts();
  }
  getAccount() : AccountInfo {
    if (this.getAccounts().length == 0) return null;
    else return this.authService.instance.getActiveAccount();
  }
  getActiveAccount() : AccountInfo {
    return this.authService.instance.getActiveAccount();
  }

  isLoggedIn(): boolean {
    return this.getAccounts().length > 0;
  }

  getAccountEmailAddress() : string {
    if (this.getAccounts().length == 0) return null;
    else if (this.getAccount().idTokenClaims.emails.length == 0) return null;
    else return this.getAccount().idTokenClaims.emails[0];
  }

  getClaims() : Claim[] {
    if (!this.isLoggedIn()) return [];
    const tokenClaims = this.getAccount()?.idTokenClaims;

    let claims: Claim[] = new Array<Claim>();

    Object.keys(tokenClaims).forEach(function (k, v) {
      let c = new Claim()
      c.id = v;
      c.claim = k;
      c.value = tokenClaims ? (tokenClaims[k] as string) : null;
      claims.push(c);
    });
    return claims;
  }

  /// Set the active account to the most recent account by authentication time.
  /// Checks if the selected account has a valid token, otherwise redirects to login page.
  private checkAndSetActiveAccount() {
    const accounts = this.authService.instance.getAllAccounts();

    if (accounts.length > 0) {
      // Find the most recent account and set as active.
      const mostRecentAccount = accounts.reduce((old, current) => {
        return old?.idTokenClaims?.auth_time ?? 0 > current?.idTokenClaims?.auth_time ?? 0 ? old : current;
      });

      this.authService.instance.setActiveAccount(mostRecentAccount);
    }
  }
}
